import { Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { useToasts } from "react-toast-notifications";
import LdcApi from "../../../LdcApis";
import ServiceSearchDropdown from "./ServiceSearchDropdown";

// ─── Validation ───────────────────────────────────────────────────────────────

const validationSchema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  mobile: yup
    .string()
    .min(10, "Min 10 characters")
    .max(12, "Max 12 characters")
    .required("Mobile is required"),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  leadSource: yup.string().required("Lead source is required"),
  notes: yup.string().optional(),
});

// ─── AddLead Modal ────────────────────────────────────────────────────────────

const AddLead = ({
  isOpen,
  onClose,
  fetchData,
  getLeadManagementTodayDetails,
  getLeadManagementMonthlyDetails,
  allService = [],
}) => {
  const { addToast } = useToasts();
  const user = JSON.parse(localStorage.getItem("user"));
  const [serviceId, setServiceId] = useState("");
  const [btnDisable, setBtnDisable] = useState(false);
  const [category, setCategory] = useState("");
  const [filterService, setFilterService] = useState([]);
  const [serviceError, setServiceError] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [storedPayload, setStoredPayload] = useState({});
  const [serviceName, setServiceName] = useState("");

  useEffect(() => {
    if (isOpen) {
      setServiceId("");
      setCategory("");
      setFilterService([]);
      setServiceError(false);
      setShowDuplicateModal(false);
      setStoredPayload({});
    }
  }, [isOpen]);

  // Filter services when category changes
  // useEffect(() => {
  //   if (category) {
  //     const filtered = allService.filter((el) => el.category === category);
  //     setFilterService(filtered);
  //     setServiceId(""); // Reset service when category changes
  //   } else {
  //     setFilterService([]);
  //     setServiceId("");
  //   }
  // }, [category, allService]);

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setServiceError(false);
  };

  const handleServiceChange = (e) => {
    setServiceId(e.target.value);
    setServiceError(false);
  };

  const capitalizeWords = (str) => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const capitalise = (val) =>
    val
      .replace(/^\s*\w/, (c) => c.toUpperCase())
      .replace(/\. +\w/g, (c) => c.toUpperCase());

      console.log('serviceId', serviceId)

  const handleSubmit = (values, { resetForm }) => {
    // Validate service selection if category is selected
    if (!serviceId) {
      setServiceError(true);
      return;
    }

    setBtnDisable(true);
    setServiceError(false);

    const payload = {
      active: true,
      firstName: capitalizeWords(values.firstName),
      lastName: capitalizeWords(values.lastName),
      mobile: values.mobile,
      telephone: "",
      email: values.email,
      leadSource: values.leadSource,
      notes: values.notes,
      status: "Open",
      leadStatus: "Open Lead",
      duplicate: false, // First attempt - check for duplicates
      leadOwner: user?.id || null,
      serviceId: serviceId ? serviceId : null,
      category: category || null,
      salutation: "",
    };

    LdcApi({
      method: "POST",
      url: "LeadManagement/UpsertLeadManagement",
      data: payload,
    })
      .then((res) => {
        if (res?.data?.status?.isSuccess) {
          addToast("Lead added successfully", { appearance: "success" });
          resetForm();
          setServiceId("");
          setCategory("");
          setFilterService([]);
          setServiceError(false);
          setBtnDisable(false);
          fetchData?.();
          getLeadManagementTodayDetails?.();
          getLeadManagementMonthlyDetails?.();
          onClose();
        } else if (
          res?.data?.status?.errorMessage ===
          "mobile or email  cannot be duplicate."
        ) {
          // Duplicate detected - show warning modal
          setBtnDisable(false);
          setStoredPayload(payload);
          setShowDuplicateModal(true);
        } else {
          addToast(res?.data?.status?.errorMessage || "Failed to add lead", {
            appearance: "error",
          });
          setBtnDisable(false);
        }
      })
      .catch((err) => {
        addToast("An error occurred. Please try again.", {
          appearance: "error",
        });
        setBtnDisable(false);
      });
  };

  const handleForceSave = () => {
    setBtnDisable(true);

    // Retry with duplicate = true
    const finalPayload = {
      ...storedPayload,
      duplicate: true,
    };

    LdcApi({
      method: "POST",
      url: "LeadManagement/UpsertLeadManagement",
      data: finalPayload,
    })
      .then((res) => {
        if (res?.data?.status?.isSuccess) {
          addToast("Lead added successfully", { appearance: "success" });
          setServiceId("");
          setCategory("");
          setFilterService([]);
          setServiceError(false);
          setBtnDisable(false);
          setShowDuplicateModal(false);
          setStoredPayload({});
          fetchData?.();
          onClose();
        } else {
          addToast(
            res?.data?.status?.errorMessage || "Failed to add duplicate lead",
            { appearance: "error" },
          );
          setBtnDisable(false);
          setShowDuplicateModal(false);
        }
      })
      .catch((err) => {
        addToast("An error occurred. Please try again.", {
          appearance: "error",
        });
        setBtnDisable(false);
        setShowDuplicateModal(false);
      });
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setStoredPayload({});
    setBtnDisable(false);
  };

  if (!isOpen) return null;

  const initialValues = {
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    leadSource: "",
    notes: "",
  };

  return (
    <>
      <style>{css}</style>

      {/* Main Add Lead Modal */}
      <div
        className="al-overlay"
        onClick={!showDuplicateModal ? onClose : undefined}
      >
        <div className="al-modal" onClick={(e) => e.stopPropagation()}>
          {/* ── Header ── */}
          <div className="al-header">
            <div className="al-header-left">
              <div className="al-header-icon">+</div>
              <div>
                <h2 className="al-title">Add New Lead</h2>
                <p className="al-subtitle">
                  Fill in the details below to create a new lead
                </p>
              </div>
            </div>
            <button
              className="al-close"
              onClick={onClose}
              aria-label="Close"
              disabled={showDuplicateModal}
            >
              ×
            </button>
          </div>

          {/* ── Formik ── */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit: formikSubmit,
              isValid,
              dirty,
            }) => {
              const canSubmit = isValid && dirty && serviceId;

              return (
                <form onSubmit={formikSubmit} className="al-form">
                  {/* ── Scrollable body ── */}
                  <div className="al-body">
                    {/* Section: Personal Info */}
                    <div className="al-section">
                      <div className="al-grid">
                        {/* First Name */}
                        <div className="al-field">
                          <label className="al-lbl">
                            First Name <span className="al-req">*</span>
                          </label>
                          <input
                            className={`al-input ${
                              touched.firstName && errors.firstName
                                ? "al-input-err"
                                : ""
                            }`}
                            name="firstName"
                            type="text"
                            placeholder="John"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            style={{ textTransform: "capitalize" }}
                          />
                          {touched.firstName && errors.firstName && (
                            <span className="al-err">{errors.firstName}</span>
                          )}
                        </div>

                        {/* Last Name */}
                        <div className="al-field">
                          <label className="al-lbl">
                            Last Name <span className="al-req">*</span>
                          </label>
                          <input
                            className={`al-input ${
                              touched.lastName && errors.lastName
                                ? "al-input-err"
                                : ""
                            }`}
                            name="lastName"
                            type="text"
                            placeholder="Smith"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            style={{ textTransform: "capitalize" }}
                          />
                          {touched.lastName && errors.lastName && (
                            <span className="al-err">{errors.lastName}</span>
                          )}
                        </div>

                        {/* Mobile */}
                        <div className="al-field">
                          <label className="al-lbl">
                            Mobile <span className="al-req">*</span>
                          </label>
                          <input
                            className={`al-input ${
                              touched.mobile && errors.mobile
                                ? "al-input-err"
                                : ""
                            }`}
                            name="mobile"
                            type="tel"
                            placeholder="07700 900000"
                            value={values.mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {touched.mobile && errors.mobile && (
                            <span className="al-err">{errors.mobile}</span>
                          )}
                        </div>

                        {/* Email */}
                        <div className="al-field">
                          <label className="al-lbl">
                            Email <span className="al-req">*</span>
                          </label>
                          <input
                            className={`al-input ${
                              touched.email && errors.email
                                ? "al-input-err"
                                : ""
                            }`}
                            name="email"
                            type="email"
                            placeholder="john.smith@email.com"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {touched.email && errors.email && (
                            <span className="al-err">{errors.email}</span>
                          )}
                        </div>

                        {/* Lead Source */}
                        <div className="al-field">
                          <label className="al-lbl">
                            Lead Source <span className="al-req">*</span>
                          </label>
                          <select
                            className={`al-select ${
                              touched.leadSource && errors.leadSource
                                ? "al-input-err"
                                : ""
                            }`}
                            name="leadSource"
                            value={values.leadSource}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Select source…</option>
                            <option value="Direct Email">Direct Email</option>
                            <option value="Phone">Phone</option>
                            <option value="Website Form">Website Form</option>
                            <option value="WhatClinic">WhatClinic</option>
                            <option value="Walk-in">Walk-in</option>
                            <option value="Referral">Referral</option>
                            <option value="Social Media">Social Media</option>
                            <option value="Google Ads">Google Ads</option>
                          </select>
                          {touched.leadSource && errors.leadSource && (
                            <span className="al-err">{errors.leadSource}</span>
                          )}
                        </div>

                        {/* Category */}
                        <div className="al-field">
                          <label className="al-lbl">Service</label>
                          <ServiceSearchDropdown
                            allService={allService}
                            serviceId={serviceId}
                            onChange={({
                              serviceId: sid,
                              serviceName: sname,
                              category: cat,
                            }) => {
                              setServiceId(sid);
                              setServiceName(sname);
                              setCategory(cat);
                              setServiceError(false);
                            }}
                            error={serviceError}
                            placeholder="Search service..."
                          />
                          {serviceError && (
                            <span className="al-err">
                              Please select a service.
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Service - only show if category is selected */}
                      {/* <div className="al-field">
                        <label className="al-lbl">Service</label>
                        <ServiceSearchDropdown
                          allService={allService}
                          serviceId={serviceId}
                          onChange={({
                            serviceId: sid,
                            serviceName: sname,
                            category: cat,
                          }) => {
                            setServiceId(sid);
                            setServiceName(sname);
                            setCategory(cat);
                            setServiceError(false);
                          }}
                          error={serviceError}
                          placeholder="Search service..."
                        />
                        {serviceError && (
                          <span className="al-err">
                            Please select a service.
                          </span>
                        )}
                      </div> */}
                    </div>

                    {/* Section: Notes */}
                    <div className="al-section">
                      <div className="al-section-header">
                        <span className="al-section-dot al-dot-green" />
                        <span className="al-section-title">
                          Notes / Comments
                        </span>
                      </div>
                      <div className="al-field">
                        <textarea
                          className="al-textarea"
                          name="notes"
                          rows={3}
                          placeholder="Add any additional notes about this lead…"
                          value={values.notes}
                          onChange={(e) =>
                            handleChange({
                              target: {
                                name: "notes",
                                value: capitalise(e.target.value),
                              },
                            })
                          }
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                  </div>
                  {/* /al-body */}

                  {/* ── Footer ── */}
                  <div className="al-footer">
                    {/* Progress hint */}
                    {/* <div className="al-progress-hint">
                      {canSubmit ? (
                        <span className="al-hint-ready">✅ Ready to save</span>
                      ) : (
                        <span className="al-hint-pending">
                          Fill in all required fields to continue
                        </span>
                      )}
                    </div> */}

                    {/* <div className="al-footer-actions"> */}
                    <button
                      type="button"
                      className="al-btn al-btn-cancel"
                      onClick={onClose}
                      disabled={showDuplicateModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="al-btn al-btn-save"
                      disabled={!canSubmit || btnDisable || showDuplicateModal}
                    >
                      {btnDisable ? "⏳ Saving…" : "Save Lead"}
                    </button>
                    {/* </div> */}
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>

      {/* Duplicate Warning Modal */}
      {showDuplicateModal && (
        <div className="al-duplicate-overlay">
          <div
            className="al-duplicate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="al-duplicate-header">
              <div className="al-duplicate-icon">⚠</div>
              <h3 className="al-duplicate-title">WARNING!</h3>
            </div>
            <div className="al-duplicate-body">
              <p className="al-duplicate-text">
                This mobile number or email already exists for a lead. Are you
                sure you wish to create a <strong>DUPLICATE</strong> entry?
              </p>
            </div>
            <div className="al-duplicate-footer">
              <button
                className="al-btn al-btn-duplicate-cancel"
                onClick={handleCancelDuplicate}
                disabled={btnDisable}
              >
                No, Cancel
              </button>
              <button
                className="al-btn al-btn-duplicate-save"
                onClick={handleForceSave}
                disabled={btnDisable}
              >
                {btnDisable ? "⏳ Saving…" : "Yes, Save Duplicate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const css = `
  /* ── Overlay ── */
  .al-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.50);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
    backdrop-filter: blur(2px);
    animation: al-fade-in 0.18s ease-out;
  }

  @keyframes al-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* ── Modal shell ── */
  .al-modal {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 680px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.18),
      0 4px 16px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    animation: al-slide-up 0.22s cubic-bezier(0.34, 1.3, 0.64, 1);
  }

  @keyframes al-slide-up {
    from { transform: translateY(20px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  /* ── Header ── */
  .al-header {
    padding: 22px 28px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
  }

  .al-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .al-header-icon {
    width: 42px;
    height: 42px;
    background: #3b82f6;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 300;
    color: #fff;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  .al-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 2px;
    line-height: 1.2;
  }

  .al-subtitle {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
  }

  .al-close {
    width: 34px;
    height: 34px;
    border: none;
    background: transparent;
    border-radius: 7px;
    cursor: pointer;
    font-size: 22px;
    line-height: 1;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    flex-shrink: 0;
  }
  .al-close:hover:not(:disabled) { background: #f1f5f9; color: #1a1a1a; }
  .al-close:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Form ── */
  .al-form {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /* ── Body ── */
  .al-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .al-body::-webkit-scrollbar { width: 6px; }
  .al-body::-webkit-scrollbar-track { background: transparent; }
  .al-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }
  .al-body::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }

  /* ── Section ── */
  .al-section {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .al-section-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .al-section-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .al-dot-blue   { background: #3b82f6; }
  .al-dot-violet { background: #8b5cf6; }
  .al-dot-green  { background: #10b981; }

  .al-section-title {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }

  /* ── Grid ── */
  .al-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 20px;
  }

  /* ── Field ── */
  .al-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .al-full {
    grid-column: 1 / -1;
  }

  .al-lbl {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
  }

  .al-req { color: #ef4444; }

  /* ── Inputs ── */
  .al-input,
  .al-select,
  .al-textarea {
    padding: 10px 13px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    color: #1a1a1a;
    background: #fff;
    outline: none;
    transition: border-color 0.18s, box-shadow 0.18s;
    width: 100%;
  }

  .al-input::placeholder,
  .al-textarea::placeholder { color: #c4cdd6; }

  .al-input:focus,
  .al-select:focus,
  .al-textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .al-input-err { border-color: #ef4444 !important; }
  .al-input-err:focus { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important; }

  .al-textarea { resize: vertical; min-height: 88px; }

  .al-err {
    font-size: 12px;
    color: #ef4444;
  }

  /* ── Footer ── */
  .al-footer {
    padding: 18px 28px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
    background: #fff;
  }

  .al-progress-hint {
    font-size: 13px;
  }

  .al-hint-ready {
    color: #10b981;
    font-weight: 600;
  }

  .al-hint-pending {
    color: #94a3b8;
  }

  .al-footer-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  /* ── Buttons ── */
  .al-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 22px;
    border-radius: 8px;
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.18s;
    line-height: 1;
    white-space: nowrap;
  }

  .al-btn-cancel {
    background: #fff;
    color: #475569;
    border: 1px solid #e2e8f0;
  }
  .al-btn-cancel:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  .al-btn-save {
    background: #3b82f6;
    color: #fff;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  }
  .al-btn-save:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
  }
  .al-btn-save:disabled {
    background: #cbd5e1;
    color: #fff;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  /* ── Duplicate Modal ── */
  .al-duplicate-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1060;
    padding: 20px;
    animation: al-fade-in 0.2s ease-out;
  }

  .al-duplicate-modal {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 460px;
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.24);
    overflow: hidden;
    animation: al-shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  @keyframes al-shake {
    10%, 90% { transform: translate3d(-1px, 0, 0); }
    20%, 80% { transform: translate3d(2px, 0, 0); }
    30%, 50%, 70% { transform: translate3d(-3px, 0, 0); }
    40%, 60% { transform: translate3d(3px, 0, 0); }
  }

  .al-duplicate-header {
    padding: 24px 28px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, #fef2f2 0%, #fff 100%);
  }

  .al-duplicate-icon {
    width: 56px;
    height: 56px;
    background: #fef2f2;
    border: 3px solid #fecaca;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #dc2626;
  }

  .al-duplicate-title {
    font-size: 20px;
    font-weight: 700;
    color: #991b1b;
    margin: 0;
    letter-spacing: 0.5px;
  }

  .al-duplicate-body {
    padding: 20px 28px 24px;
  }

  .al-duplicate-text {
    font-size: 14px;
    line-height: 1.6;
    color: #334155;
    margin: 0;
    text-align: center;
  }

  .al-duplicate-text strong {
    color: #dc2626;
    font-weight: 700;
  }

  .al-duplicate-footer {
    display: flex;
    gap: 10px;
    padding: 16px 28px 24px;
  }

  .al-btn-duplicate-cancel {
    background: #fff;
    color: #475569;
    border: 1px solid #e2e8f0;
    flex: 1;
  }
  .al-btn-duplicate-cancel:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  .al-btn-duplicate-save {
    background: #dc2626;
    color: #fff;
    box-shadow: 0 2px 8px rgba(220, 38, 38, 0.25);
    flex: 1;
  }
  .al-btn-duplicate-save:hover:not(:disabled) {
    background: #b91c1c;
    box-shadow: 0 4px 14px rgba(220, 38, 38, 0.35);
    transform: translateY(-1px);
  }
  .al-btn-duplicate-save:disabled {
    background: #cbd5e1;
    color: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  /* ── Responsive ── */
  @media (max-width: 580px) {
    .al-modal  { max-height: 100vh; border-radius: 0; }
    .al-grid   { grid-template-columns: 1fr; }
    .al-footer { flex-direction: column; align-items: stretch; }
    .al-footer-actions { justify-content: flex-end; }
    .al-duplicate-modal { max-width: 95vw; }
  }
`;

export default AddLead;
